import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import api from '../../services/api'

const TagsContext = createContext()

const getStoredToken = () => {
  if (typeof window === 'undefined') {
    return null
  }

  const storedToken = localStorage.getItem('token')
  if (!storedToken) {
    return null
  }

  try {
    const parsedToken = JSON.parse(storedToken)
    return typeof parsedToken === 'string' ? parsedToken : null
  } catch (_error) {
    return storedToken
  }
}

const isJwtExpired = token => {
  if (!token || typeof token !== 'string') {
    return true
  }

  const tokenParts = token.split('.')
  if (tokenParts.length !== 3) {
    return false
  }

  try {
    const payload = JSON.parse(atob(tokenParts[1]))
    const exp = Number(payload?.exp)
    if (!exp) {
      return false
    }

    return Date.now() >= exp * 1000
  } catch (_error) {
    return false
  }
}

export const TagsProvider = ({ children }) => {
  const [allTagsCache, setAllTagsCache] = useState([])
  const [contactTags, setContactTags] = useState({})
  const [ticketTags, setTicketTags] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const dispatchTagEvent = useCallback((eventType, detail) => {
    const event = new CustomEvent(eventType, {
      detail: { ...detail, timestamp: Date.now() }
    })
    window.dispatchEvent(event)
  }, [])

  const fetchAndSetTags = useCallback(async (type, id) => {
    if (!id) return []
    setIsLoading(true)
    try {
      if (type === 'contact') {
        const { data } = await api.get(`/contacts/${id}`)
        const fetchedTags = (data?.tags || []).map(tag => ({
          ...tag,
          tipo: 'contato'
        }))
        setContactTags(prev => ({ ...prev, [id]: fetchedTags }))
        return fetchedTags
      } else if (type === 'ticket') {
        const { data } = await api.get(`/tickets/${id}`)
        const fetchedTags = (data?.tags || []).map(tag => ({
          ...tag,
          tipo: 'atendimento'
        }))
        setTicketTags(prev => ({ ...prev, [id]: fetchedTags }))
        return fetchedTags
      }
    } catch (err) {
      console.error(`Failed to fetch ${type} tags for id ${id}`, err)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  const fetchAllTags = useCallback(async () => {
    const token = getStoredToken()
    if (!token || isJwtExpired(token)) {
      setAllTagsCache([])
      return
    }

    try {
      const { data } = await api.get('/tags/list')
      setAllTagsCache(data)
    } catch (err) {
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        setAllTagsCache([])
        return
      }
      console.error('Failed to load all tags', err)
    }
  }, [])

  useEffect(() => {
    fetchAllTags()
  }, [fetchAllTags])

  const getTags = useCallback(
    (type, id) => {
      if (type === 'contact') {
        return contactTags[id] || []
      } else if (type === 'ticket') {
        return ticketTags[id] || []
      }
      return []
    },
    [contactTags, ticketTags]
  )

  const addTag = useCallback(
    async (type, id, tagId) => {
      if (!id || !tagId) return false

      // Encontra a tag no cache ou busca na API
      let tagToAdd = allTagsCache.find(tag => String(tag.id) === String(tagId))
      if (!tagToAdd) {
        try {
          const { data } = await api.get(`/tags/${tagId}`)
          tagToAdd = data
          setAllTagsCache(prevCache =>
            prevCache.some(t => String(t.id) === String(tagId))
              ? prevCache
              : [...prevCache, tagToAdd]
          )
        } catch (err) {
          console.error(`Failed to fetch tag ${tagId}`, err)
          return false
        }
      }

      const currentTags = getTags(type, id)
      if (currentTags.some(t => String(t.id) === String(tagId))) {
        return true
      }

      const optimisticTags = [
        ...currentTags,
        { ...tagToAdd, tipo: type === 'contact' ? 'contato' : 'atendimento' }
      ]
      if (type === 'contact') {
        setContactTags(prev => ({ ...prev, [id]: optimisticTags }))
      } else if (type === 'ticket') {
        setTicketTags(prev => ({ ...prev, [id]: optimisticTags }))
      }

      try {
        if (type === 'contact') {
          await api.post(`/contacts/${id}/tags`, { tagId })
        } else if (type === 'ticket') {
          await api.post(`/tags/sync`, { ticketId: id, tags: optimisticTags })
        }

        dispatchTagEvent(`${type}TagsChanged`, {
          [`${type}Id`]: id,
          tags: optimisticTags
        })
        return true
      } catch (error) {
        console.error(`Failed to add ${type} tag:`, error)
        // Reverte o estado otimista em caso de erro
        if (type === 'contact') {
          setContactTags(prev => ({ ...prev, [id]: currentTags }))
        } else if (type === 'ticket') {
          setTicketTags(prev => ({ ...prev, [id]: currentTags }))
        }
        // Tenta refazer a busca para garantir a consistência
        await fetchAndSetTags(type, id)
        return false
      }
    },
    [allTagsCache, getTags, dispatchTagEvent, fetchAndSetTags]
  )

  const removeTag = useCallback(
    async (type, id, tagId) => {
      if (!id || !tagId) return false

      const currentTags = getTags(type, id)
      const optimisticTags = currentTags.filter(
        tag => String(tag.id) !== String(tagId)
      )

      if (type === 'contact') {
        setContactTags(prev => ({ ...prev, [id]: optimisticTags }))
      } else if (type === 'ticket') {
        setTicketTags(prev => ({ ...prev, [id]: optimisticTags }))
      }

      try {
        if (type === 'contact') {
          await api.delete(`/contacts/${id}/tags/${tagId}`)
        } else if (type === 'ticket') {
          await api.post(`/tags/sync`, { ticketId: id, tags: optimisticTags })
        }
        dispatchTagEvent(`${type}TagsChanged`, {
          [`${type}Id`]: id,
          tags: optimisticTags
        })
        return true
      } catch (error) {
        console.error(`Failed to remove ${type} tag:`, error)
        // Reverte o estado otimista em caso de erro
        if (type === 'contact') {
          setContactTags(prev => ({ ...prev, [id]: currentTags }))
        } else if (type === 'ticket') {
          setTicketTags(prev => ({ ...prev, [id]: currentTags }))
        }
        // Tenta refazer a busca para garantir a consistência
        await fetchAndSetTags(type, id)
        return false
      }
    },
    [getTags, dispatchTagEvent, fetchAndSetTags]
  )

  const getAllTags = useCallback(() => allTagsCache, [allTagsCache])

  const value = useMemo(
    () => ({
      contactTags,
      ticketTags,
      getContactTags: id => getTags('contact', id),
      getTicketTags: id => getTags('ticket', id),
      addTag,
      removeTag,
      getAllTags,
      fetchAndSetTags,
      fetchAllTags,
      isLoading
    }),
    [
      contactTags,
      ticketTags,
      getTags,
      addTag,
      removeTag,
      getAllTags,
      fetchAndSetTags,
      fetchAllTags,
      isLoading
    ]
  )

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>
}

export const useTags = () => useContext(TagsContext)
export default TagsContext
